import { useEffect, useRef } from 'react';

/**
 * Provides a boolean indicating whether the page was hard-reloaded and calls
 * `onReloadDetected` exactly once on that first render so the consumer can
 * perform cleanup (e.g., clear URL params).
 */
export const useSoftReload = (onReloadDetected?: () => void): boolean => {
  // Persisted value, evaluated once during initial render.
  const isReloadingRef = useRef<boolean>(
    typeof window !== 'undefined' && sessionStorage.getItem('isReloading') === 'true'
  );

  // Set flag before the page unloads so we can detect reloads.
  useEffect(() => {
    const handler = () => sessionStorage.setItem('isReloading', 'true');
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Clear the flag and invoke callback once after reload is detected.
  useEffect(() => {
    if (isReloadingRef.current) {
      sessionStorage.removeItem('isReloading');
      onReloadDetected?.();
      // Prevent subsequent re-renders from thinking it's still a reload.
      isReloadingRef.current = false;
    }
  }, [onReloadDetected]);

  return isReloadingRef.current;
}; 